import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Modal from './ui/Modal';
import Button from './ui/Button';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLogin: () => void;
  onSwitchToRegister?: () => void;
}

export const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose, onLogin, onSwitchToRegister }) => {
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      await login(username, password);
      onLogin();
      setUsername('');
      setPassword('');
      onClose();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Login failed. Please check your credentials and try again.';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setUsername('');
    setPassword('');
    setError('');
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Welcome back"
      description="Your cars, collection and community are right here."
      size="sm"
    >
      <form onSubmit={handleSubmit} className="space-y-4" autoComplete="on" method="post">
        <div>
          <label htmlFor="login-username" className="input-label">Username</label>
          <input
            type="text"
            id="login-username"
            name="username"
            data-testid="login-username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="input"
            placeholder="Enter username"
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck="false"
            required
          />
        </div>

        <div>
          <div className="flex items-center justify-between gap-3">
            <label htmlFor="login-password" className="input-label mb-0">Password</label>
            <Link
              to="/forgot-password"
              className="text-xs text-orange-300 hover:text-orange-200"
              onClick={onClose}
            >
              Forgot password?
            </Link>
          </div>
          <input
            type="password"
            id="login-password"
            name="password"
            data-testid="login-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input mt-1"
            placeholder="Enter password"
            autoComplete="current-password"
            required
          />
        </div>

        {error && <div className="alert-error" role="alert">{error}</div>}

        <div className="flex gap-3 pt-1">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            className="flex-1 min-h-[44px]"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={isLoading}
            className="flex-1 min-h-[44px]"
            data-testid="login-submit"
          >
            Sign in
          </Button>
        </div>
      </form>

      {onSwitchToRegister && (
        <p className="mt-6 text-center text-sm text-gray-400">
          New to Bilt:Spec?{' '}
          <button
            type="button"
            onClick={onSwitchToRegister}
            className="text-orange-400 hover:text-orange-300 font-medium transition-colors"
          >
            Join Bilt
          </button>
        </p>
      )}
    </Modal>
  );
};
